import React, {useEffect, useState} from "react";
import {NoteNode, NoteTag} from "../../model/node";
import {Navigate, useParams, useSearchParams} from "react-router-dom";
import {getItem, postRaw} from "../../utils/communication";
import ErrorDisplay from "../../components/error-display";
import BreadCrumbs from "../../components/note/breadcrumbs";
import nanoid from "../../utils/nanoid";
import HFInput from "../../components/form/hf-input";
import HfUnmarked from "../../components/form/hf-unmarked";
import HFSelect from "../../components/form/hf-select";
import HFStepper from "../../components/form/hf-stepper";
import parseAnError from "../../model/misc";

export default function NodeEditPage() {
    let path = useParams()["*"] ?? "";

    const [node, setNode] = useState<NoteNode | null>(null),
        [tags, setTags] = useState<NoteTag[]>([]),
        [error, setError] = useState<string | null>(null),
        [formData, setFormData] = useState({
            publicId: "",
            name: "",
            description: "",
            content: "",
            tagId: "",
            path: path,
            permission: 0,
            nodeType: "Directory",
        }),
        [needsRedirect, setNeedsRedirect] = useState<boolean>(false);


    const isModeNew = useSearchParams()[0].get("mode") === "new";

    useEffect(() => {
        document.title = "Nodes - Hoarfroster";

        (async () => {
            let response = await getItem<NoteTag[]>("/note/tag/all");
            if (response.code !== 200) {
                setError(parseAnError(response.data).message);
            } else {
                const tags = response.data as NoteTag[];
                setTags(tags);

                if (isModeNew && tags.length > 0) {
                    setFormData({
                        ...formData,
                        tagId: tags[0].publicId
                    })
                }
            }
        })();

        if (isModeNew) {
            setFormData({
                ...formData,
                publicId: nanoid(),
            })
        }
    }, [isModeNew]);

    useEffect(() => {
        if (path !== "") {
            getNode().then(() => {
            });
        } else {
            setError("Invalid Path")
        }

        setFormData({...formData, path: path});
    }, [path]);

    const getNode = async () => {
        let response = await getItem<NoteNode>(`/note/note/${path}:/`);

        if (response.code !== 200) {
            setError(parseAnError(response.data).message);
        } else {
            const node = response.data as NoteNode

            setNode(node);
            if (!isModeNew) {
                setFormData({
                    publicId: node.publicId,
                    name: node.name,
                    description: node.description,
                    content: node.content,
                    tagId: node.tagId,
                    path: node.path,
                    permission: node.permission,
                    nodeType: node.nodeType,
                })
            }
        }
    }


    const onSubmit = async (event: React.FormEvent) => {
        event.preventDefault();

        let response = await postRaw(`/note/note/${isModeNew ? formData.path : path}:/`, {
            ...formData,
            label: getLabel(formData.path),
            createdAt: node?.createdAt,
            updatedAt: node?.updatedAt,
            deletedAt: node?.deletedAt,
            publicId: isModeNew ? nanoid() : node?.publicId
        });

        if (response.code !== 200) {
            setError(parseAnError(response.data).message);
        } else {
            setNeedsRedirect(true);
        }

        return;
    }

    const handleInputChange = (event: { target: { name: any; value: any; }; }) => {
        const {name, value} = event.target;
        setFormData({...formData, [name]: value});
    };

    const handleSelectChange = (name: string, value: string) => {
        setFormData({...formData, [name]: value});
    }

    const handleStepperChange = (name: string, value: number) => {
        setFormData({...formData, [name]: value});
    }

    const buildForm = () => {
        if (node) {
            return (
                <>
                    <HFInput type="text" name="name" value={formData.name} onChange={handleInputChange}
                             placeholder="Name" label="Name"/>
                    <HFInput type="text" name="path" value={formData.path} onChange={handleInputChange}
                             placeholder="Path" label="Path" tips={<code>Label: {getLabel(formData.path)}</code>}/>
                    <HfUnmarked name="description" value={formData.description} onChange={handleInputChange}
                                placeholder="Description"
                                label="Description"/>
                    <HfUnmarked name="content" value={formData.content} onChange={handleInputChange}
                                placeholder="Content"
                                label="Content"/>
                    <HFSelect name="nodeType" value={formData.nodeType} onChange={handleSelectChange}
                              label="Node Type"
                              options={[{value: "Note", label: "Note"}, {value: "Directory", label: "Directory"}]}/>
                    <HFStepper name="permission" value={formData.permission} step={1} range={[0, 3]}
                               onChange={handleStepperChange} label="Permission"/>
                    <HFSelect name="tagId" value={formData.tagId} onChange={handleSelectChange}
                              label="Tag"
                              options={tags.map(tag => {
                                  return {value: tag.publicId, label: tag.name};
                              })}/>
                </>
            )
        } else {
            return (<p>Loading...</p>)
        }
    }

    const getLabel = (path: string) => {
        let segments = path.split(".");
        return segments.length > 0 ? segments[segments.length - 1] : "";
    }

    return (
        <main>
            <div className="container">
                <div className="content space-y-4">
                    <BreadCrumbs path={path}/>

                    <form onSubmit={onSubmit}>
                        {buildForm()}

                        <div className="actions">
                            <button type="submit">Save</button>
                            <button type="reset">Reset</button>
                        </div>
                    </form>

                    {needsRedirect && <Navigate to={"/note/" + (formData.path)}/>}

                    <ErrorDisplay error={error}/>
                </div>

                <div className="sidebar space-y-2">
                    <h2>Tips</h2>
                    <p>如果没有登录，你可能无法看得到图片。</p>
                </div>
            </div>
        </main>
    );
}